import { useEffect, useState } from "react";
import { Gara } from "../types/gare/gara";
import { GaraService } from "../services/garaService";
import { Concorrente } from "../types/concorrenti/concorrente";
import { CarpResultEntry, ResultService } from "../services/resultService";
import { formatKg } from "../types/fishingDiscipline";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface Props {
  gara: Gara;
  pendingCompetitors: Concorrente[];
  onRegistrationsSaved: () => void;
}

export default function CarpResultsTab({ gara, pendingCompetitors, onRegistrationsSaved }: Props) {
  const [competition, setCompetition] = useState(gara);
  const [entries, setEntries] = useState<CarpResultEntry[]>([]);
  const [published, setPublished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([GaraService.getGaraById(gara.id), ResultService.getCarpResults(gara.id)])
      .then(([fresh, response]) => {
        if (!active) return;
        const competitors = [...fresh.competitors, ...pendingCompetitors.filter(c => !fresh.competitors.some(existing => existing.id === c.id))];
        setCompetition({ ...fresh, competitors });
        setPublished(response.resultsPublished);
        setEntries(competitors.map(c => ({
          ...(response.results.find(e => e.competitorId === c.id)
            ?? { competitorId: c.id, weightKg: null, tieOrder: null }),
          isAbsent: false,
        })));
      }).catch(e => { if (active) setError(e.message || "Impossibile caricare i risultati."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [gara.id, pendingCompetitors]);

  const update = (id: number, changes: Partial<CarpResultEntry>) => {
    setEntries(previous => previous.map(e => e.competitorId === id ? { ...e, ...changes } : e));
    setError(""); setMessage("");
  };
  const ordered = [...entries].filter(e => e.weightKg !== null)
    .sort((a, b) => b.weightKg! - a.weightKg! || (a.tieOrder ?? Infinity) - (b.tieOrder ?? Infinity) || a.competitorId - b.competitorId);
  const save = async (publish: boolean) => {
    setSaving(true); setError(""); setMessage("");
    try {
      await ResultService.saveCarpResults(gara.id, { publish, results: entries, addCompetitorIds: pendingCompetitors.map(c => c.id) });
      setPublished(previous => previous || publish);
      onRegistrationsSaved();
      setMessage(published || publish ? "Classifica salvata. Campionato aggiornato." : "Bozza salvata.");
    } catch (e: any) { setError(e.message || "Impossibile salvare i risultati."); }
    finally { setSaving(false); }
  };
  const exportPdf = () => {
    const doc = new jsPDF();
    doc.text(`${competition.name} - Carpe e Carassi`, 14, 18);
    autoTable(doc, { startY: 26, head: [["Posizione", "Concorrente", "Peso (kg)"]],
      body: ordered.map(e => {
        const competitor = competition.competitors.find(c => c.id === e.competitorId)!;
        return [String(ordered.indexOf(e) + 1),
          `${competitor.lastName} ${competitor.firstName}`, formatKg(e.weightKg!)];
      }) });
    doc.save(`${competition.name}-carpe.pdf`);
  };

  if (loading) return <p role="status">Caricamento risultati…</p>;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3"><div><h3 className="text-xl font-semibold">Classifica Carpe e Carassi</h3>
        <p className="text-sm text-gray-600 mt-1">{published ? "Pubblicata. Ogni modifica aggiorna automaticamente il campionato." : "Bozza. Completa gli esiti e gli spareggi prima di pubblicare."}</p>
      </div><span className={`rounded-full px-3 py-1 text-xs font-semibold ${published ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>{published ? "Pubblicata" : "Bozza"}</span></div>
      <p className="text-sm text-gray-600">Inserisci il peso di ogni partecipante, anche se è 0 kg. Chi non partecipa va rimosso dalla scheda Concorrenti.</p>
      {error && <p role="alert" className="text-red-700">{error}</p>}
      {message && <p role="status" className="text-green-700">{message}</p>}
      {pendingCompetitors.length > 0 && <p className="text-sm text-brand-700">Completa i pesi dei nuovi concorrenti e salva per aggiornare gara e campionato.</p>}
      {entries.length === 0 ? <p className="text-gray-600">Iscrivi almeno un concorrente per inserire i pesi.</p> : (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="hidden items-center gap-4 bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 lg:flex">
            <span className="w-10 text-center">Pos.</span><span className="min-w-0 flex-1">Concorrente</span><span className="w-32">Peso (kg)</span><span className="w-32">Spareggio</span>
          </div>
          {entries.map(entry => {
            const competitor = competition.competitors.find(c => c.id === entry.competitorId)!;
            const name = `${competitor.lastName} ${competitor.firstName}`;
            const tied = entry.weightKg !== null && entries.filter(e => e.weightKg === entry.weightKg).length > 1;
            const position = ordered.findIndex(e => e.competitorId === entry.competitorId) + 1;
            const background = position === 1 ? "bg-yellow-100" : position === 2 ? "bg-gray-200" : position === 3 ? "bg-orange-100" : "bg-white";
            return <div key={entry.competitorId} className={`border-t border-gray-200 px-4 py-4 first:border-t-0 ${background}`}>
              <div className="flex flex-wrap items-center gap-4 lg:flex-nowrap">
                <span className="w-10 shrink-0 text-center text-lg font-bold text-gray-800">{position ? `${position}°` : "—"}</span>
                <div className="min-w-0 flex-1 font-semibold text-gray-900 break-words">{name}
                </div>
                <div className="grid w-full grid-cols-1 items-end gap-4 sm:grid-cols-2 lg:contents">
                  <label className="block text-sm lg:w-32"><span className="block mb-1 text-xs font-medium text-gray-600 lg:sr-only">Peso (kg)</span>
                    <input aria-label={`Peso in kg di ${name}`} type="number" min="0" max="999999999.999" step="0.001" disabled={saving}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-100 disabled:text-gray-400" value={entry.weightKg ?? ""}
                      onChange={e => update(entry.competitorId, { weightKg: e.target.value === "" ? null : Number(e.target.value), isAbsent: false, tieOrder: null })} />
                  </label>
                  <div className="lg:w-32">
                    {tied ? <label className="block text-sm"><span className="mb-1 block text-xs font-medium text-gray-600">Ordine a pari peso</span>
                      <input aria-label={`Ordine di spareggio di ${name}`} type="number" min="1" step="1" disabled={saving}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500" value={entry.tieOrder ?? ""}
                        onChange={e => update(entry.competitorId, { tieOrder: e.target.value === "" ? null : Number(e.target.value) })} />
                    </label> : <span className="hidden text-gray-400 lg:block">—</span>}
                  </div>
                </div>
              </div>
            </div>;
          })}
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <button disabled={saving || entries.length === 0} onClick={() => save(false)} className="rounded bg-brand-600 text-white px-4 py-2 disabled:opacity-50">{saving ? "Salvataggio…" : published ? "Salva modifiche" : "Salva bozza"}</button>
        {!published && <button disabled={saving || entries.length === 0} onClick={() => save(true)} className="rounded bg-brand-700 text-white px-4 py-2 disabled:opacity-50">Pubblica classifica</button>}
        <button disabled={saving || ordered.length === 0} onClick={exportPdf} className="rounded border px-4 py-2 disabled:opacity-50">Esporta PDF</button>
      </div>
    </div>
  );
}
